import { differenceInSeconds } from '@portal/utils/util-date';
import { guardEmptyArray, guardUnspecified } from '@portal/utils/util-guards';
import { localStorageApiFactory } from '@portal/utils/util-storage';
import { Regions } from '@smh/projects/regions';

import { PersonalSelectionHttpClient } from './personal-selection.api';
import { presentPersonalSelectionBlock } from './personal-selection.presenter';
import { PersonalSelectionBlock } from './personal-selection.vm';

const PERSONAL_SELECTION_DATA_LS_KEY = 'jtnews_personal_selection_data';
const TEN_MINUTES_IN_SECONDS = 60 * 10;

type PersonalSelectionLSData = {
  data: PersonalSelectionBlock;
  expiry: number;
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface IPersonalSelectionUseCase {
  getPersonalSelectionBlockData: (
    regionId: Regions
  ) => Promise<PersonalSelectionBlock | null>;
  getSavedPersonalSelectionBlockData: () => PersonalSelectionBlock | null;
  savePersonalSelectionBlockData: (data: PersonalSelectionBlock) => void;
}

export class PersonalSelectionUseCase implements IPersonalSelectionUseCase {
  // В дроне валиться на имени приватных переменных, хотя все нормально должно быть
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly _httpClient: PersonalSelectionHttpClient;
  // eslint-disable-next-line
  private _localStorage: ReturnType<typeof localStorageApiFactory>;

  constructor(envType: string) {
    this._httpClient = new PersonalSelectionHttpClient({
      envType,
      protocol: 'https'
    });
    this._localStorage = localStorageApiFactory(localStorage);
  }

  public async getPersonalSelectionBlockData(
    regionId: Regions
  ): Promise<PersonalSelectionBlock | null> {
    try {
      const blockDTO = await this._httpClient.fetchPersonalSelection({ regionId });
      const blockVM = presentPersonalSelectionBlock(blockDTO);
      if (guardEmptyArray(blockVM.data)) {
        this.savePersonalSelectionBlockData(blockVM);
      }

      return blockVM;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  public getSavedPersonalSelectionBlockData(): PersonalSelectionBlock | null {
    try {
      const blockDTO = this._localStorage.get<PersonalSelectionLSData>(
        PERSONAL_SELECTION_DATA_LS_KEY
      );

      if (!guardUnspecified(blockDTO)) {
        return null;
      }

      const expiryDiff = differenceInSeconds(Date.now(), Number(blockDTO.expiry));
      if (expiryDiff >= TEN_MINUTES_IN_SECONDS) {
        this._localStorage.remove(PERSONAL_SELECTION_DATA_LS_KEY);
        return null;
      }

      return blockDTO.data;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  public savePersonalSelectionBlockData(data: PersonalSelectionBlock): void {
    if (!guardUnspecified(this._localStorage)) {
      return;
    }

    this._localStorage.set<PersonalSelectionLSData>(PERSONAL_SELECTION_DATA_LS_KEY, {
      data,
      expiry: Date.now()
    });
  }
}
